import { Component, OnDestroy } from '@angular/core';
import { AlertController, IonicModule, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { SideMenuComponent } from './shared/components/side-menu/side-menu.component';
import { Network } from '@capacitor/network';
import { NetworkService } from 'src/app/core/errors/network.service';
import { errorMessage } from 'src/app/core/errors/error-message';
import { UiUtilityService } from './core/services/ui-utility.service';
import { CapacitorJailbreakRootDetection } from '@akilrafeek/capacitor-jailbreak-root-detection';
import { PushNotifications } from '@capacitor/push-notifications';
import { Device, DeviceInfo } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { TextZoom } from '@capacitor/text-zoom';

import { ToastController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { BackgroundStateService } from './core/services/background.service';
import { DeviceService } from './core/services/device.service';
import { UserAgentService } from './core/services/user-agent.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonicModule, SideMenuComponent],
})
export class AppComponent implements  OnDestroy {
  private networkSubscription: Subscription | null = null;
  private networkAlertTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private translate: TranslateService,
    private platform: Platform,
    private networkService: NetworkService,
    private uiUtilityService: UiUtilityService,
    private alertController: AlertController,
    private toastController: ToastController,
    private bg: BackgroundStateService,
    private deviceService: DeviceService,
    private userAgentService: UserAgentService
  ) {
    this.translate.setDefaultLang('en');
    this.translate.use('en');
    this.platform.ready().then(() => {
      // this.monitorNetworkStatusChange();
      this.logCurrentNetworkStatus();
      this.checkForRootedDevice();
      this.setupForegroundHandler();
      this.deviceService.getDeviceId();
      this.userAgentService.getUA();
      this.lockAndroidFontScale();
    });
    this.alertController.create({ animated: false }).then((t: any) => {
      t.present();
      t.dismiss();
    });

    this.platform.pause.subscribe(() => {
      this.bg.setBackgroundState(true);
    });

    this.platform.resume.subscribe(() => {
      this.bg.setBackgroundState(false);
    });
  }

  setupForegroundHandler() {
    PushNotifications.addListener(
      'pushNotificationReceived',
      async (notification) => {
        console.log('🔥 Foreground notification:', notification);

        const data = notification.data;
        const type = data?.type;

        // Only show toast for ALERT type (your choice)
        if (type === 'ALERT') {
          const toast = await this.toastController.create({
            header: data.title,
            message: data.body,
            duration: 5000,
            position: 'top',
            color: 'primary', // or 'dark', 'success', etc.
            buttons: [
              {
                text: 'OK',
                role: 'cancel',
              },
            ],
          });

          await toast.present();
        }

        // Ignore info notifications
        if (type === 'INFO') {
          console.log('ℹ️ Info notification ignored in foreground');
        }
      }
    );
  }

  async checkForRootedDevice() {
    const isRooted =
      await CapacitorJailbreakRootDetection.isJailbrokenOrRooted();
    if (isRooted?.result) {
      // CapacitorJailbreakRootDetection.exitApp();
    }
  }


  /**
   * Subscribes to network status change messages and manages display of a network alert.
   * Display No internet Connection alert if device is offline for more than 5 seconds.
   * @returns void
   */
  monitorNetworkStatusChange() {
    this.networkSubscription = this.networkService
      .getStatusChangeMsg()
      .subscribe((res) => {
        // Always clear any pending timeout
        if (this.networkAlertTimeout) {
          clearTimeout(this.networkAlertTimeout);
          this.networkAlertTimeout = null;
        }
        if (res?.connected) {
          this.uiUtilityService.dismissErrorAlert();
        } else if (res && !res.connected) {
          if (!this.networkAlertTimeout) {
            this.networkAlertTimeout = setTimeout(() => {
              this.uiUtilityService.showAlert(errorMessage.noNetwork, 'Alert');
              this.networkAlertTimeout = null; // Reset after showing alert
            }, 5000);
          }
        }
      });
  }

  logCurrentNetworkStatus = async () => {
    const status = await Network.getStatus();
    if (!status.connected) {
      this.uiUtilityService.showAlert(errorMessage.noNetwork, 'Alert');
    }
  };

  ngOnDestroy(): void {
    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
    }
    if (this.networkAlertTimeout) {
      clearTimeout(this.networkAlertTimeout);
    }
  }
  async lockAndroidFontScale() {
    if (Capacitor.getPlatform() === 'android') {
      try {
        await TextZoom.set({ value: 1.0 });
      } catch (error) {
        console.error('TextZoom plugin failed to lock font scale:', error);
      }
    }
  }
}
